import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { debounce, uniqBy } from 'lodash';
import useCommonState, {
  baseValidationSchema,
  type PersonaScimFormPayload,
  type BaseStateProps,
  type MobxProps,
  type CommonFormProps,
} from './useCommonState';
import { type Inputs } from './Input';
import { APPLY_ALL_VALUE } from 'admin/shared-components/MultiSelect/MultiSelect';
import { useGetScimGroupsQuery } from 'redux-stores/scim/scimAPI';

const scimValidationSchema = baseValidationSchema.extend({
  scimGroups: z.array(z.string()).min(1, 'Select at least one SCIM Group.'),
});

const useSCIMPersona = ({
  selectedPersona,
  setToastMessageData,
  isPersonaMessageTtlAndPinLockEnabled,
  editMode,
  setSelectedPersona,
  updatePersona,
  createPersona,
  setPersonaFormState,
  findSCIMGroups,
}: BaseStateProps & MobxProps & CommonFormProps) => {
  const {
    canRevert,
    commonFormFields,
    errors,
    loading: commonLoading,
    revertChanges,
    selectedOrganizationId,
    setCanRevert,
    setCommonFormFields,
    setErrors,
    setTemplate,
    setUserPermissions,
    setUserSettings,
    template,
    userPermissions,
    userSettings,
    validateForm,
  } = useCommonState<'scim'>({
    selectedPersona,
    setToastMessageData,
    isPersonaMessageTtlAndPinLockEnabled,
  });

  const [loading, setLoading] = useState(false);

  const { data: scimGroupsData, isLoading: scimGroupsLoading } = useGetScimGroupsQuery({
    groupIds: selectedPersona?.groupIds || [],
    organizationId: selectedOrganizationId,
  });

  const [scimGroupState, setScimGroupState] = useState<{
    selectedGroupsIds: string[];
    selectedGroupsData: { label: string; value: string }[];
    availableGroups: { label: string; value: string }[];
  }>({
    selectedGroupsIds: selectedPersona?.groupIds || [],
    selectedGroupsData: [],
    availableGroups: [],
  });

  useEffect(() => {
    if (scimGroupsData && !scimGroupsLoading) {
      setScimGroupState((currentState) => {
        const options = scimGroupsData.map((group) => ({
          label: group.displayName,
          value: group.id,
        }));
        return {
          ...currentState,
          selectedGroupsData: uniqBy([...currentState.selectedGroupsData, ...options], 'value'),
          availableGroups: uniqBy([...currentState.availableGroups, ...options], 'value'),
        };
      });
    }
  }, [scimGroupsData, scimGroupsLoading]);

  const handleSearch = useCallback(
    async ({ search }: { search?: string }) => {
      const { results } = await findSCIMGroups(selectedOrganizationId, search);
      setScimGroupState((currentState) => {
        if (results?.length > 0) {
          const options = uniqBy(
            [
              ...currentState.selectedGroupsData,
              ...results.map((group) => ({
                label: group.displayName,
                value: group.id,
              })),
            ],
            'value'
          );
          return {
            ...currentState,
            availableGroups: options,
          };
        } else {
          return currentState;
        }
      });
    },
    [findSCIMGroups, selectedOrganizationId]
  );

  const debouncedSearch = debounce(async (query?: string) => {
    await handleSearch({ search: query });
  }, 300);

  const handleValueChange = useCallback((value: { label: string; value: string }[]) => {
    if (value.length === 0) {
      setScimGroupState((currentState) => ({
        ...currentState,
        selectedGroupsIds: [],
        selectedGroupsData: [],
      }));
    } else {
      const lastItem = value[value.length - 1];
      setScimGroupState((currentState) => {
        if (lastItem.value === APPLY_ALL_VALUE) {
          const availableGroups = currentState.availableGroups.filter(
            (group) => !currentState.selectedGroupsData.some((g) => g.value === group.value)
          );
          const newGroupsData = [...currentState.selectedGroupsData, ...availableGroups];
          return {
            ...currentState,
            selectedGroupsIds: newGroupsData.map((group) => group.value),
            selectedGroupsData: newGroupsData,
          };
        } else if (!currentState.selectedGroupsIds.includes(lastItem.value)) {
          return {
            ...currentState,
            selectedGroupsIds: [...currentState.selectedGroupsIds, lastItem.value],
            selectedGroupsData: [...currentState.selectedGroupsData, lastItem],
          };
        } else if (value.length === currentState.selectedGroupsIds.length - 1) {
          return {
            ...currentState,
            selectedGroupsIds: value.map((group) => group.value),
            selectedGroupsData: value,
          };
        }
        return currentState;
      });
    }
  }, []);

  const schema = useMemo(() => {
    const schema: Inputs[] = [
      {
        label: 'Name: ',
        type: 'input',
        optionName: 'name',
        dataTestId: 'newPersonaId',
        onInputChange: (value: string) =>
          setCommonFormFields((currentValues) => ({ ...currentValues, name: value })),
        disabled: !editMode,
        id: 'name',
        value: commonFormFields.name,
      },
      {
        label: 'Description: ',
        type: 'input',
        optionName: 'description',
        dataTestId: 'persona description',
        onInputChange: (value: string) =>
          setCommonFormFields((currentValues) => ({ ...currentValues, description: value })),
        disabled: !editMode,
        id: 'description',
        value: commonFormFields.description || '',
      },
      {
        label: 'SCIM Groups: ',
        optionName: 'scimGroups',
        type: 'multiSelect',
        listOptions: scimGroupState.availableGroups,
        dataTestId: 'scimGroupsDropdown',
        onInputChange: () => {
          return;
        },
        disabled: !editMode,
        id: 'scimGroups',
        value: scimGroupState.selectedGroupsIds,
        placeholder: 'Select SCIM Groups...',
        customOnSearch: (search: string) => {
          debouncedSearch(search);
        },
        customOnValueChange: (value) => {
          handleValueChange(value);
        },
      },
      {
        type: 'select',
        label: 'Template: ',
        optionName: 'template',
        dataTestId: 'templateDropdown',
        onInputChange: (template) => setTemplate(template),
        disabled: !editMode,
        id: 'template',
        placeholder: 'Select a template...',
        skip: !!selectedPersona,
      },
    ];
    return schema;
  }, [
    commonFormFields.description,
    commonFormFields.name,
    debouncedSearch,
    editMode,
    handleValueChange,
    scimGroupState.availableGroups,
    scimGroupState.selectedGroupsIds,
    selectedPersona,
    setCommonFormFields,
    setTemplate,
  ]);

  const loadRequirements = useCallback(async () => {
    setLoading(true);
    try {
      await handleSearch({});
    } finally {
      setLoading(false);
    }
  }, [handleSearch, setLoading]);

  useEffect(() => {
    loadRequirements();
  }, [loadRequirements]);

  const savePersona = async (persona: PersonaScimFormPayload) => {
    try {
      const baseFieldsValues = {
        name: persona.name || '',
        description: persona.description || '',
        groupIds: scimGroupState.selectedGroupsIds || [],
        titles: [],
        personaType: 'GROUP' as const,
      };
      if (selectedPersona) {
        await updatePersona(
          {
            ...baseFieldsValues,
            permissions: userPermissions,
            settings: userSettings,
          },
          selectedPersona.personaId,
          selectedOrganizationId
        );
        setSelectedPersona(undefined);
        setToastMessageData({ type: 'SUCCESS', message: 'Persona updated' });
      } else {
        await createPersona(
          {
            ...baseFieldsValues,
            parentPersonaId: template?.personaId,
            permissions: userPermissions,
            settings: userSettings,
          },
          selectedOrganizationId
        );
        setToastMessageData({ type: 'SUCCESS', message: 'Persona created' });
      }
      setPersonaFormState({ display: false, type: 'title' });
      setErrors({});
    } catch (error) {
      let errorMessage = 'Response error please try again.';
      if (error?.response?.status === 409) {
        errorMessage = error?.response?.text;
      }
      setErrors((prev) => ({ ...prev, general: errorMessage }));
      setToastMessageData({ type: 'FAILURE', message: errorMessage });
    }
  };

  const onSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const success = validateForm(
      {
        name: commonFormFields.name,
        description: commonFormFields.description,
        scimGroups: scimGroupState.selectedGroupsIds,
      },
      scimValidationSchema
    );
    if (!success) return;
    savePersona({
      name: commonFormFields.name,
      description: commonFormFields.description,
      scimGroups: [],
    });
  };
  return {
    canRevert,
    errors,
    loading: commonLoading || loading || scimGroupsLoading,
    onSave,
    revertChanges,
    schema,
    scimGroupState,
    setCanRevert,
    setUserPermissions,
    setUserSettings,
    userPermissions,
    userSettings,
  };
};

export default useSCIMPersona;
